import { useState, useEffect, useContext, ReactNode } from "react";
import { Box, Typography, Button, Stack } from "@mui/material";
import { ImportWizardContext } from "components/importer/ImportWizardContext";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { useTranslation } from "react-i18next";
import { WIZARD_ACTION, DataWidget } from "types";
import { ImportFieldInput } from "./ImportFieldInput";
dayjs.extend(customParseFormat);

interface GenerateDataWidgetProps {
  setDataWidget: (value: DataWidget) => void;
}

export const GenerateDataWidget = <F extends string>({
  setDataWidget,
}: GenerateDataWidgetProps): ReactNode => {
  const { t } = useTranslation();
  const { state, dispatch, fieldMetadata } = useContext(ImportWizardContext);
  const [newValue, setNewValue] = useState("");

  // this component is not used if the user has not selected a system field
  if (!state.selectedSystemField) {
    setDataWidget("none");
  }

  const systemField = state.selectedSystemField;
  const { validator, type, enumValues } = fieldMetadata[systemField];

  useEffect(() => {
    // set default value for date fields when generating data
    if (type === "date") {
      setNewValue(dayjs().toISOString());
    } else if (type === "enum" && enumValues && enumValues.length > 0) {
      setNewValue(enumValues[0]);
    } else if (type === "string") {
      setNewValue("unknown");
    }
  }, [systemField]);

  const handleGenerateData = (field: F, value: string) => {
    dispatch({
      type: WIZARD_ACTION.GENERATE_DATA,
      payload: { field, value },
    });
    setDataWidget("preview");
  };

  if (!state.selectedSystemField) return null;
  return (
    <Stack direction="column" spacing={0}>
      <Typography variant="subtitle2" width="100%" textAlign="center" py={1}>
        {t("generate_data", { ns: "importWizard" })}
      </Typography>
      <Stack direction="row" spacing={3} display="flex" pb={2}>
        <Box sx={{ flex: 1 }}>
          <ImportFieldInput
            field={systemField}
            value={newValue}
            setValue={setNewValue}
          />
        </Box>
      </Stack>
      <Stack
        direction="row"
        gap={4}
        display="flex"
        justifyContent="space-between"
      >
        <Button
          onClick={() => handleGenerateData(systemField, newValue)}
          variant="text"
          color="primary"
          disabled={!validator(newValue)}
        >
          {t("generate_data", { ns: "importWizard" })}
        </Button>
      </Stack>
    </Stack>
  );
};
